import {
    BxfChatBubble,
    BxfChatBubbleType,
    BxfChatPromptCarousel,
    BxfIcon,
    BxfIconTypeEnum,
} from '@blk/bxf-react-components';

import styled from '@emotion/styled';
import { useMemo, useRef } from 'react';
import { ChatExchange } from '../components';
import {
    CHAT_BLK_PANEL_TAGLINE,
    CHAT_PROMPT_CAROUSEL_DATA_LINK_EVENT,
    ChatContentConstants,
    WELCOME_MESSAGE_BODY,
    WELCOME_MESSAGE_BOLD,
} from '../constants';
import { useChatBlkStore, useStarterPrompts } from '../hooks';
import { QueryState } from '../types';
import { uuidv4 } from '../utils';

const { CANVAS_CHAT_SLOT, CHAT_BUBBLE_CONTENT_SLOT } = ChatContentConstants;

const ResponseQueryContainer = styled.div`
    font-size: 16px; // $space-s
    line-height: 24px; // $space-m
`;
const WelcomeChatBubble = styled(BxfChatBubble)`
    padding: 16px 0; // $space-s
`;
const WelcomeMessageHeader = styled.span`
    font-weight: 700;
`;

const StarterPrompts = styled.div<{ isVisible: boolean }>`
    display: flex;
    opacity: ${(props) => (props.isVisible ? 1 : 0)};
    transition: all 0.5s ease-in-out;
    height: ${(props) => (props.isVisible ? '104px' : 0)};
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
`;

const ChatContainer = styled.div`
    display: flex !important;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    overflow: visible;
`;

const ChatContent = styled.div`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 4rem auto;
    overflow: visible;
`;

// the padding div at the bottom of the chat content to hoist the last chat bubble class
const PaddedDiv = styled.div<{ paddingHeight: number }>`
    height: ${(props) => props.paddingHeight}px;
`;

const MessageContainer = styled.div`
    padding: 1rem 0;
`;

const Tagline = styled.p`
    color: #000;
    text-align: center;
    font-family: 'BLK Fort';
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 100% */
`;

/**
 * ChatContent component renders the chat interface including welcome message and chat exchanges
 * between the user and the ChatBlk service.
 *
 * @returns {JSX.Element} The rendered chat content component.
 * @component
 */
const ChatContentPage = (): JSX.Element => {
    const { queries, paddingHeight } = useChatBlkStore();
    const topCarouselEl = useRef<HTMLBxfChatPromptCarouselElement>();
    const bottomCarouselEl = useRef<HTMLBxfChatPromptCarouselElement>();

    const { topHalf, bottomHalf, scrollVelocity, handlePromptSelected } =
        useStarterPrompts();

    const hasQueries = useMemo(
        () =>
            queries?.ids?.some(
                (query) => queries.entries[query]?.state !== QueryState.CANCELED
            ) || false,
        [queries]
    );

    return (
        <ChatContainer slot={CANVAS_CHAT_SLOT}>
            <div>
                <ChatContent>
                    <div>
                        <BxfIcon
                            type={BxfIconTypeEnum['bxf-panel-logo']}
                        ></BxfIcon>
                    </div>
                    <Tagline>{CHAT_BLK_PANEL_TAGLINE}</Tagline>
                </ChatContent>
                <StarterPrompts isVisible={!hasQueries}>
                    <BxfChatPromptCarousel
                        ref={topCarouselEl}
                        scrollVelocity={scrollVelocity}
                        data={topHalf}
                        dataLinkEvent={CHAT_PROMPT_CAROUSEL_DATA_LINK_EVENT}
                        onPromptSelected={handlePromptSelected}
                        onMouseEnter={() => {
                            setTimeout(() => {
                                topCarouselEl.current?.stopRotation();
                                bottomCarouselEl.current?.stopRotation();
                            }, 250);
                        }}
                        onMouseLeave={() => {
                            topCarouselEl.current?.startRotation();
                            bottomCarouselEl.current?.startRotation();
                        }}
                        onFocus={() => {
                            setTimeout(() => {
                                topCarouselEl.current?.stopRotation();
                                bottomCarouselEl.current?.stopRotation();
                            }, 250);
                        }}
                        onBlur={() => {
                            topCarouselEl.current?.startRotation();
                            bottomCarouselEl.current?.startRotation();
                        }}
                    />
                    <BxfChatPromptCarousel
                        ref={bottomCarouselEl}
                        scrollVelocity={-scrollVelocity}
                        data={bottomHalf}
                        dataLinkEvent={CHAT_PROMPT_CAROUSEL_DATA_LINK_EVENT}
                        onPromptSelected={handlePromptSelected}
                        onMouseEnter={() => {
                            setTimeout(() => {
                                topCarouselEl.current?.stopRotation();
                                bottomCarouselEl.current?.stopRotation();
                            }, 250);
                        }}
                        onMouseLeave={() => {
                            topCarouselEl.current?.startRotation();
                            bottomCarouselEl.current?.startRotation();
                        }}
                        onFocus={() => {
                            setTimeout(() => {
                                topCarouselEl.current?.stopRotation();
                                bottomCarouselEl.current?.stopRotation();
                            }, 500);
                        }}
                        onBlur={() => {
                            topCarouselEl.current?.startRotation();
                            bottomCarouselEl.current?.startRotation();
                        }}
                    />
                </StarterPrompts>
            </div>
            <MessageContainer>
                <WelcomeChatBubble type={BxfChatBubbleType.RESPONSE}>
                    <ResponseQueryContainer slot={CHAT_BUBBLE_CONTENT_SLOT}>
                        <WelcomeMessageHeader>{`${WELCOME_MESSAGE_BOLD} `}</WelcomeMessageHeader>
                        {WELCOME_MESSAGE_BODY}
                    </ResponseQueryContainer>
                </WelcomeChatBubble>
                {queries?.ids?.map((hashId: string) => (
                    <ChatExchange
                        key={hashId || uuidv4()}
                        hashId={hashId}
                        state={queries?.entries[hashId]?.state}
                    />
                )) || <></>}
                <PaddedDiv paddingHeight={paddingHeight} />
            </MessageContainer>
        </ChatContainer>
    );
};

export default ChatContentPage;
